import { ApiConfig, GlobalSettingsUpdate, CustomerSettingsUpdate } from '../types/types';

// Change from absolute URL to relative URL for production deployment
// const API_BASE_URL = 'http://localhost:8080';
const API_BASE_URL = ''; 
const API_KEY = 'adminadmin';

class ApiService {
  private static instance: ApiService;
  private constructor() {}

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  private getHeaders(): HeadersInit {
    return {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'apikey': API_KEY
    };
  }

  async getExtendedConfig(): Promise<ApiConfig> {
    const response = await fetch(`${API_BASE_URL}/getextendedconfig`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch extended configuration: ${response.status}`);
    }

    const data = await response.json();
    
    // Transform the response to match our expected type
    return {
      globalSettings: {
        globalReadBatchSize: data.globalReadBatchSize,
        globalReadDelayMillis: data.globalReadDelayMillis,
        globalPostURL: data.globalPostURL,
        logLevel: data.logLevel,
        artificialDelaySetting: data.artificialDelaySetting
      },
      customers: data.customers
    };
  }

  async removeCustomer(apikey: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/removecustomer`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ apikey })
    });

    if (!response.ok) {
      throw new Error(`Failed to remove customer: ${response.status}`);
    }
  }

  async flushQueue(apikey: string, dateUntil: string): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/deletemessages`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify({
          apikey,
          dateUntil
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to flush queue');
      }

      return await response.json();
    } catch (error) {
      console.error('Error flushing queue:', error);
      throw error;
    }
  }

  async getRocksDBStats(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/rocksdbstats`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to get RocksDB stats: ${response.status}`);
    }

    return response.json();
  }

  async triggerCompaction(): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/compact`, {
      method: 'POST',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error(`Failed to trigger compaction: ${response.status}`);
    }
  }

  async postConfig(config: { globalSettings?: GlobalSettingsUpdate; customers?: CustomerSettingsUpdate[] }): Promise<void> {
    console.log('Making postConfig API call with:', config);
    
    // Transform the config to match backend expectations
    const transformedConfig = {
      ...config.globalSettings,  // Spread global settings to root level
      customers: config.customers
    };
    
    console.log('Transformed config for API:', transformedConfig);
    
    const response = await fetch(`${API_BASE_URL}/postconfig`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(transformedConfig)
    });

    console.log('postConfig response status:', response.status);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('postConfig error response:', errorText);
      throw new Error(`Failed to update configuration: ${response.status} - ${errorText}`);
    }
    console.log('postConfig successful');
  }

  async fetchMessages(apiKey: string, startKey?: string, limit?: string): Promise<any> {
    let url = `${API_BASE_URL}/messages?apikey=${apiKey}`;
    
    // Add optional query parameters if provided
    if (startKey) {
      url += `&startKey=${startKey}`;
    }
    if (limit) {
      url += `&limit=${limit}`;
    }
    
    const response = await fetch(url, {
      headers: this.getHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch messages: ${response.status}`);
    }
    
    return response.json();
  }
}

export const apiService = ApiService.getInstance(); 